import './NumberInput.scss';

import cx from 'classnames';
import type { ChangeEvent } from 'react';
import React, { memo, useEffect, useState } from 'react';

import bem from '../../utils/bem';

/**
 * A clamping edge of the input: a number, or `null` for "no limit on this side" (KATANA-1812).
 *
 * `null` and `undefined` are deliberately different. `undefined` is an omitted prop and still falls
 * back to the 0-100 defaults below, so every existing caller keeps clamping. Only an explicit `null`
 * removes the edge, which is what a filter whose catalog config unsets that bound passes.
 */
type NumberInputBound = number | null;

type NummberInputProps = {
  value: number | null;
  onChange: (newValue: number | null) => void;
  className?: string;
  emptyValue?: number | null;
  max?: NumberInputBound;
  min?: NumberInputBound;
  decimals?: number;
  icon?: React.JSX.Element;
  prefixIcon?: React.JSX.Element;
};

const DEFAULT_EMPTY_VALUE = 0;
const DEFAULT_MAX_VALUE = 100;
const DEFAULT_MIN_VALUE = 0;

const KEPT_DECIMAL_PATTERN = /^-?\d*\.\d*$/;

const [block, element] = bem('number-input');

/** Clamps `parsed` into [min, max], skipping either edge that is null (unbounded). */
function clampToBounds(parsed: number, min: NumberInputBound, max: NumberInputBound): number {
  if (max !== null && parsed > max) return max;
  if (min !== null && parsed < min) return min;
  return parsed;
}

/** Digits after the decimal point, or -1 when there is no decimal point. */
function fractionLength(input: string): number {
  const point = input.indexOf('.');
  return point === -1 ? -1 : input.length - point - 1;
}

/**
 * Whether an integer-only input accepts text with a decimal point: only while it trims the decimal
 * part of a value that already had one, so a kept decimal value stays editable and no keystroke
 * adds decimals to it.
 */
function isTrimmingKeptDecimals(next: string, current: string): boolean {
  return KEPT_DECIMAL_PATTERN.test(next) && fractionLength(next) <= fractionLength(current);
}

function NumberInput({
  value,
  onChange,
  className,
  emptyValue,
  max = DEFAULT_MAX_VALUE,
  min = DEFAULT_MIN_VALUE,
  decimals = 0,
  icon,
  prefixIcon,
}: Readonly<NummberInputProps>): JSX.Element {
  if (min !== null && max !== null && min > max) {
    throw new Error('Max should be greater than min!');
  }

  const [rawInput, setRawInput] = useState<string>(value !== null && value !== undefined ? String(value) : '');

  useEffect(() => {
    setRawInput(value !== null && value !== undefined ? String(value) : '');
  }, [value]);

  const commitDecimalText = (text: string): void => {
    const parsed = Number.parseFloat(text);
    if (Number.isNaN(parsed)) return;

    const clamped = clampToBounds(parsed, min, max);

    if (clamped !== parsed) {
      setRawInput(String(clamped));
    }
    onChange(clamped);
  };

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();

    const inputStr = e.target?.value ?? '';

    if (inputStr === '') {
      const emptyNumber = emptyValue === undefined ? DEFAULT_EMPTY_VALUE : emptyValue;
      setRawInput('');
      onChange(emptyNumber);
      return;
    }

    if (decimals > 0) {
      const decimalPattern = new RegExp(String.raw`^-?\d*\.?\d{0,${decimals}}$`);
      if (!decimalPattern.test(inputStr)) return;

      setRawInput(inputStr);

      if (inputStr.endsWith('.') || inputStr === '-' || inputStr === '-.') return;

      commitDecimalText(inputStr);
    } else if (inputStr.includes('.')) {
      if (!isTrimmingKeptDecimals(inputStr, rawInput)) return;

      setRawInput(inputStr);

      commitDecimalText(inputStr);
    } else {
      if (min === null && inputStr === '-') {
        setRawInput(inputStr);
        return;
      }

      const parsed = Number.parseInt(inputStr);

      if (Number.isNaN(parsed)) return;

      const numberValue = clampToBounds(parsed, min, max);

      setRawInput(String(numberValue));
      onChange(numberValue);
    }
  };

  return (
    <div className={cx(block(), className)} data-testid="number-input-block">
      {prefixIcon && <span className={element('prefix-icon')}>{prefixIcon}</span>}
      <input
        data-testid="number-input-field"
        value={rawInput}
        onChange={onChangeHandler}
        className={cx(element('input'), prefixIcon ? element('input', 'has-prefix') : undefined)}
        type="text"
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      />
      {icon && <span className={element('icon')}>{icon}</span>}
    </div>
  );
}

export default memo(NumberInput);
